/**
 * AdUnit — the shared wrapper for one Google AdSense <ins> unit, used by
 * InArticleAd and MultiplexAd (which only differ in the <ins> attributes).
 *
 * Layout: the wrapper has zero height (no label, no margin) until AdSense
 * marks the <ins> data-ad-status="filled"; an "unfilled" unit is hidden
 * (styles in index.css, .ad-slot). The status is mirrored onto the wrapper
 * so the CSS also works in browsers without :has().
 *
 * Mount it with a route-based `key` so SPA navigation gets a fresh <ins>
 * (and so one push() per page view).
 *
 * `gap` = vertical space around a filled unit (CSS length, default 2.5rem).
 * `insProps` = the format-specific <ins> attributes (style, data-ad-*).
 */
import { useEffect, useRef, useState } from 'react';

const AdUnit = ({ client, slot, position, gap, testId, className = '', insProps }) => {
  const insRef = useRef(null);
  const [status, setStatus] = useState('');

  useEffect(() => {
    const ins = insRef.current;
    if (!ins) return undefined;
    const sync = () => setStatus(ins.getAttribute('data-ad-status') || '');
    let observer = null;
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(sync);
      observer.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] });
    }
    // One push per <ins>; AdSense marks processed units with
    // data-adsbygoogle-status, which also guards StrictMode's double effect.
    if (!ins.getAttribute('data-adsbygoogle-status')) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        // Script blocked (ad blocker, offline) or unit already filled.
      }
    }
    return () => observer?.disconnect();
  }, []);

  return (
    <aside
      className={`ad-slot not-prose${className ? ` ${className}` : ''}`}
      style={gap ? { '--ad-gap': gap } : undefined}
      data-ad-status={status || undefined}
      data-ad-position={position}
      data-testid={testId}
      aria-label="Advertisement"
    >
      <div className="ad-slot__label">Advertisement</div>
      <ins ref={insRef} className="adsbygoogle" {...insProps} data-ad-client={client} data-ad-slot={slot} />
    </aside>
  );
};

export default AdUnit;
